import React, { useState, useEffect, useRef, useCallback } from "react";
import { Sparkles, RefreshCw, AlertTriangle, Bot, Target, Timer } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

// Minimal markdown renderer: ## headings, **bold**, and - bullets.
function renderMarkdown(text) {
  const lines = (text || "").split("\n");
  const out = [];
  let bullets = [];
  const flush = (key) => {
    if (bullets.length) {
      out.push(
        <ul key={`ul-${key}`} className="my-1.5 space-y-1 pl-1">
          {bullets.map((b, i) => (
            <li key={i} className="flex gap-2 text-[13px] leading-relaxed" style={{ color: "var(--text-muted)" }}>
              <span style={{ color: "var(--accent-soft)" }}>•</span>
              <span>{inline(b)}</span>
            </li>
          ))}
        </ul>
      );
      bullets = [];
    }
  };
  const inline = (s) => {
    const parts = s.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g).filter(Boolean);
    return parts.map((p, i) => {
      if (p.startsWith("**") && p.endsWith("**"))
        return <strong key={i} style={{ color: "var(--text)" }}>{p.slice(2, -2)}</strong>;
      if (p.startsWith("*") && p.endsWith("*"))
        return <em key={i} style={{ color: "var(--text)" }}>{p.slice(1, -1)}</em>;
      return <span key={i}>{p}</span>;
    });
  };
  lines.forEach((raw, idx) => {
    const line = raw.trim();
    if (!line) { flush(idx); return; }
    if (/^#{1,6}\s/.test(line)) {
      flush(idx);
      out.push(
        <h4 key={`h-${idx}`} className="mt-3 mb-1 text-[13px] font-bold tracking-wide flex items-center gap-1.5" style={{ color: "var(--accent-soft)" }}>
          {line.replace(/^#{1,6}\s/, "")}
        </h4>
      );
    } else if (/^[-*]\s/.test(line)) {
      bullets.push(line.replace(/^[-*]\s/, ""));
    } else {
      flush(idx);
      out.push(
        <p key={`p-${idx}`} className="text-[13px] leading-relaxed my-1" style={{ color: "var(--text-muted)" }}>
          {inline(line)}
        </p>
      );
    }
  });
  flush("end");
  return out;
}

export default function AIMarketOutlook({ symbol }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deepDive, setDeepDive] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const loadingRef = useRef(false);

  const generate = useCallback(async (dd = deepDive) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const res = await api.getMarketOutlook(symbol, dd);
      setData(res);
      (res.newAlerts || []).forEach((a) => toast.warning(a.title, { description: a.message }));
      if (res.newAlerts && res.newAlerts.length) {
        window.dispatchEvent(new Event("outlook-alerts-updated"));
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not generate outlook. Try again.";
      setError(msg);
      toast.error("AI outlook failed");
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [symbol, deepDive]);

  // Optional auto-refresh every 3 minutes while enabled.
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => generate(), 180000);
    return () => clearInterval(id);
  }, [autoRefresh, generate]);

  return (
    <div className="mt-4 rounded-2xl overflow-hidden" style={{ background: "var(--panel)", border: "1px solid var(--border)" }} data-testid="ai-market-outlook">
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5" style={{ borderBottom: "1px solid var(--border)" }}>
        <div className="flex items-center gap-2.5">
          <div className="grid place-items-center rounded-lg" style={{ width: 32, height: 32, background: "linear-gradient(135deg,#8b5cf6,#6d28d9)" }}>
            <Bot size={17} color="#fff" />
          </div>
          <div>
            <h3 className="text-[15px] font-bold flex items-center gap-2">
              AI Market Outlook
              <span className="pill text-[9px] font-bold" style={{ color: "var(--accent-soft)", background: "var(--panel-2)", borderColor: "var(--border)" }}>GPT-5.4</span>
            </h3>
            <p className="text-[11px]" style={{ color: "var(--text-dim)" }}>Live narrative from your pairs, scores & plans · not financial advice</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Market-wide vs Deep-dive toggle */}
          <div className="flex items-center rounded-lg p-0.5" style={{ background: "var(--panel-2)", border: "1px solid var(--border)" }} data-testid="outlook-mode-toggle">
            <button
              data-testid="mode-market"
              onClick={() => setDeepDive(false)}
              className="px-2.5 h-7 rounded-md text-[11px] font-semibold transition-colors"
              style={{ background: !deepDive ? "linear-gradient(90deg,rgba(139,92,246,.25),rgba(139,92,246,.08))" : "transparent", color: !deepDive ? "#fff" : "var(--text-dim)" }}
            >
              Market
            </button>
            <button
              data-testid="mode-deepdive"
              onClick={() => setDeepDive(true)}
              className="flex items-center gap-1 px-2.5 h-7 rounded-md text-[11px] font-semibold transition-colors"
              style={{ background: deepDive ? "linear-gradient(90deg,rgba(139,92,246,.25),rgba(139,92,246,.08))" : "transparent", color: deepDive ? "#fff" : "var(--text-dim)" }}
            >
              <Target size={12} /> {symbol} Deep Dive
            </button>
          </div>

          {/* Auto-refresh toggle */}
          <button
            data-testid="auto-refresh-toggle"
            onClick={() => setAutoRefresh((v) => !v)}
            title="Auto-refresh every 3 minutes"
            className="flex items-center gap-1.5 px-2.5 h-8 rounded-lg text-[11px] font-semibold transition-colors"
            style={{
              background: autoRefresh ? "rgba(34,197,94,.12)" : "var(--panel-2)",
              border: `1px solid ${autoRefresh ? "rgba(34,197,94,.35)" : "var(--border)"}`,
              color: autoRefresh ? "var(--green)" : "var(--text-dim)",
            }}
          >
            <Timer size={13} className={autoRefresh ? "animate-pulse" : ""} /> Auto {autoRefresh ? "ON" : "OFF"}
          </button>

          <button
            data-testid="generate-outlook-btn"
            onClick={() => generate()}
            disabled={loading}
            className="flex items-center gap-2 rounded-lg px-3.5 h-8 text-[13px] font-semibold transition-transform active:scale-95 disabled:opacity-60"
            style={{ background: "linear-gradient(135deg,#8b5cf6,#6d28d9)", color: "#fff" }}
          >
            {loading ? <RefreshCw size={15} className="animate-spin" /> : data ? <RefreshCw size={15} /> : <Sparkles size={15} />}
            {loading ? "Analyzing…" : data ? "Refresh" : "Generate"}
          </button>
        </div>
      </div>

      <div className="px-5 py-4 min-h-[80px]">
        {loading && (
          <div className="space-y-2 animate-pulse" data-testid="outlook-loading">
            {[90, 75, 82, 60].map((w, i) => (
              <div key={i} className="h-3 rounded" style={{ width: `${w}%`, background: "var(--panel-2)" }} />
            ))}
          </div>
        )}
        {!loading && error && (
          <div className="flex items-center gap-2 text-[13px]" style={{ color: "var(--red)" }} data-testid="outlook-error">
            <AlertTriangle size={15} /> {error}
          </div>
        )}
        {!loading && !error && data && (
          <div data-testid="outlook-content">
            {renderMarkdown(data.outlook)}
            <div className="mt-3 pt-2.5 text-[10px] flex items-center justify-between" style={{ borderTop: "1px solid var(--border)", color: "var(--text-dim)" }}>
              <span>{data.deepDive ? `Deep dive: ${data.focus}` : data.focus ? `Focus: ${data.focus}` : "Market-wide"}{autoRefresh ? " · auto-refresh on" : ""}</span>
              <span>Generated {new Date(data.generatedAt).toLocaleTimeString()}</span>
            </div>
          </div>
        )}
        {!loading && !error && !data && (
          <div className="flex flex-col items-center justify-center py-6 text-center" data-testid="outlook-empty">
            <Sparkles size={24} style={{ color: "var(--accent-soft)" }} />
            <p className="mt-2 text-[13px]" style={{ color: "var(--text-muted)" }}>
              {deepDive
                ? <>Deep dive into <strong style={{ color: "var(--text)" }}>{symbol}</strong>'s active 4H plan — entry, stop & targets.</>
                : <>Get an AI-generated read on the market{symbol ? <> — with focus on <strong style={{ color: "var(--text)" }}>{symbol}</strong></> : ""}.</>}
            </p>
            <p className="text-[11px] mt-0.5" style={{ color: "var(--text-dim)" }}>Click Generate to start.</p>
          </div>
        )}
      </div>
    </div>
  );
}
